import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { propOr } from 'ramda';
import { useLocation, useParams } from 'react-router';
import { determineLocale, getLocale } from '../utils/determineLocale';
import { LEGACY_SUPPORTED_LANGUAGES_KEY, RTL_LANG } from '../constants/i18n';
import {
  ReTargetContainer,
  ReTargetContentContainer,
  StyledButton,
  ConsentsButtonContainer,
  Title,
  Body,
  ToggleLayout,
  Footer,
  StyledAccordion,
  ToggleLabelText,
  ToggleLabelHeader,
  Graphic,
  WjaContainer,
  WjaToggleHeader,
  WjaToggleHeaderTitle,
  WjaToggleBody,
  WjaToggleBodyExtens,
  WjaToggleBodyExtensContainer,
  WjaToggleBodyExtensContainerDesc,
  WjaToggleBodyExtensContainerButton,
  WjaToggleBodyExtensContainerButtonText
} from '../styles/privacySettingsStyles';
import IconMegaphone from '@veneer/core/dist/scripts/icons/icon_megaphone';
import IconShield from '@veneer/core/dist/scripts/icons/icon_shield';
import IconJobs from '@veneer/core/dist/scripts/icons/icon_jobs';
import IconShare from '@veneer/core/dist/scripts/icons/icon_share';
import Toggle from '@veneer/core/dist/scripts/toggle';
import svgAnalytics from '../assets/analytics.svg';
import svgPersonalized from '../assets/personalized.svg';
import { IOS, ANDROID } from '../constants/purposeID';
import { renderTrackEvent } from '../lib/analyticsTracking';
import PrivacyConsents from './PrivacyConsents';

const PrivacySettings = ({
  handleClick,
  backClick,
  analytics = false,
  advertising = false,
  personalizedSuggestions = false,
  dataTransfer = false,
  setAnalytics,
  setAdvertising,
  setPersonalizedSuggestions,
  setDataTransfer,
  csData = {},
  loading,
  appAnalyticsContent = '',
  advertisingContent = '',
  personalizedSuggestionsContent = '',
  dataTransferContent = '',
  smallButton,
  query,
  printerWelcome,
  seteasyStartProOperation,
  easyStartProUsage = false,
  seteasyStartProUsage,
  easyStartProOperationContent = '',
  easyStartProUsageContent = '',
  defaultConsentContent = '',
  seteasyStartBasic,
  easyStartUsage = false,
  seteasyStartUsage,
  easyStartBasicContent = '',
  easyStartUsageContent = ''
}) => {
  const { pathname, search } = useLocation();
  const { platform } = useParams();
  const userConsents = pathname.includes('user-consents');
  const onBoard = search.includes('onBoard=true');
  const title = propOr('', 'title', csData);
  const body = propOr('', 'body', csData);
  const footer = propOr('', 'footer', csData);
  const appRequiredTitle = propOr('', 'requiredTitle', csData);
  const appOptionalTitle = propOr('', 'optionalTitle', csData);
  const appOptionalDataUseDesc = propOr('', 'optionalDataUseDesc', csData);
  const appDataTransferTitle = propOr('', 'dataTransferTitle', csData);
  const appAnalyticsTitle = propOr('', 'analyticsTitle', csData);
  const advertisingTitle = propOr('', 'advertisingTitle', csData);
  const easyStartProRequiredText = propOr('', 'required', csData);
  const easyStartProAnalyticsText = propOr('', 'analyticsTitle', csData);
  const easyStartRequiredText = propOr('', 'required', csData);
  const easyStartAnalyticsText = propOr('', 'analyticsTitle', csData);
  const personalizedTitle = propOr('', 'personalizedTitle', csData);
  const continueButton = propOr('', 'continueButton', csData);
  const backButton = propOr('', 'backButton', csData);
  const saveButton = propOr('', 'saveButton', csData);
  const isDesktop = platform === IOS || platform === ANDROID ? false : true;
  const isIOS = platform === IOS ? true : false;
  const queryParams = new URLSearchParams(search);
  const isShowBackButton = queryParams.get('secondaryButton') !== 'none' ? true : false;
  const { language } = getLocale(LEGACY_SUPPORTED_LANGUAGES_KEY);
  const { country } = determineLocale(LEGACY_SUPPORTED_LANGUAGES_KEY);
  const isRTL = RTL_LANG.includes(language) ? true : false;
  const queryParameter = query.appName || '';
  const hasAppNameEasyStart = queryParameter.toLowerCase() === 'easystart';
  const hasAppNameEasyStartPro = queryParameter.toLowerCase() === 'easystartpro';
  const [isRenderContinueButton, setRenderContinueButton] = useState(false);
  const [isRenderPageAnyToggleClick, setRenderPageAnyToggleClick] = useState(false);
  const [isEnableContinueButton, setEnableContinueButton] = useState(false);
  const { ffAnalyticsTracking } = useFlags();

  const queryParam = query.appName || '';
  const hasAppNameWJA = queryParam.toLowerCase() === 'wja';

  useEffect(() => {
    if (userConsents && onBoard && !isRenderContinueButton && csData !== null) {
      renderTrackEvent(ffAnalyticsTracking);
      setRenderContinueButton(true);
    }
  }, [csData]);

  useEffect(() => {
    if (userConsents && isRenderPageAnyToggleClick && csData !== null) {
      setEnableContinueButton(true);
    }
  }, [isRenderPageAnyToggleClick]);

  const EasyStartPrivacyConsents = () => {
    return (
      <React.Fragment>
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartBasicContent }} />
              ),
              header: {
                startArea: <IconShield />,
                centralArea: <ToggleLabelHeader>{easyStartRequiredText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="basic-easystart-toggle"
                    data-testid="basic-easystart-toggle"
                    defaultOn
                    disabled
                    onChange={(e) => {
                      seteasyStartBasic(e);
                    }}
                  />
                )
              },
              id: 'basic-easystart-accordion-item',
              expanded: true,
              disabled: true
            }
          ]}
          id="basic-easystart-items"
        />
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartUsageContent }} />
              ),
              header: {
                startArea: <Graphic id="usage-analytics" src={svgAnalytics} alt="analytics" />,
                centralArea: <ToggleLabelHeader>{easyStartAnalyticsText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="easystart-analytics-toggle"
                    data-testid="easystart-analytics-toggle"
                    defaultOn={easyStartUsage}
                    onChange={(e) => {
                      seteasyStartUsage(e);
                    }}
                  />
                )
              },
              id: 'easystart-analytics-accordion-item',
              expanded: true
            }
          ]}
          id="easystart-consents-items"
        />
      </React.Fragment>
    );
  };

  const EasyStartProPrivacyConsents = () => {
    return (
      <React.Fragment>
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText
                  dangerouslySetInnerHTML={{ __html: easyStartProOperationContent }}
                />
              ),
              header: {
                startArea: <IconShield />,
                centralArea: <ToggleLabelHeader>{easyStartProRequiredText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="basic-easystartpro-toggle"
                    data-testid="basic-easystartpro-toggle"
                    defaultOn
                    disabled
                    onChange={(e) => {
                      seteasyStartProOperation(e);
                    }}
                  />
                )
              },
              id: 'basic-easystartpro-accordion-item',
              expanded: true,
              disabled: true
            }
          ]}
          id="basic-easystartpro-items"
        />
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartProUsageContent }} />
              ),
              header: {
                startArea: <Graphic id="usage-analytics" src={svgAnalytics} alt="analytics" />,
                centralArea: <ToggleLabelHeader>{easyStartProAnalyticsText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="easystartpro-analytics-toggle"
                    data-testid="easystartpro-analytics-toggle"
                    defaultOn={easyStartProUsage}
                    onChange={(e) => {
                      seteasyStartProUsage(e);
                    }}
                  />
                )
              },
              id: 'easystartpro-analytics-accordion-item',
              expanded: true
            }
          ]}
          id="easystartpro-consents-items"
        />
      </React.Fragment>
    );
  };

  return (
    <ReTargetContainer
      id="settings-container"
      data-testid="privacy-settings"
      isDesktop={isDesktop}
      isRTL={isRTL}
    >
      <ReTargetContentContainer id="content-container" isIOS={isIOS}>
        <Title
          id="settings-container-title"
          isDesktop={isDesktop}
          dangerouslySetInnerHTML={{ __html: title }}
          isRTL={isRTL}
        />
        <Body
          id="settings-container-body"
          isDesktop={isDesktop}
          isWJA={hasAppNameWJA}
          dangerouslySetInnerHTML={{ __html: body }}
          isRTL={isRTL}
        />
        <ToggleLayout id="settings-container-toggle-layout" isDesktop={isDesktop}>
          {(() => {
            if (hasAppNameEasyStart) {
              return <EasyStartPrivacyConsents isDesktop={isDesktop} />;
            } else if (hasAppNameEasyStartPro) {
              return <EasyStartProPrivacyConsents isDesktop={isDesktop} />;
            } else {
              return (
                <PrivacyConsents
                  isPipl={country === 'cn'}
                  isDesktop={isDesktop}
                  appRequiredTitle={appRequiredTitle}
                  appAnalyticsContent={appAnalyticsContent}
                  advertisingContent={advertisingContent}
                  analytics={analytics}
                  advertising={advertising}
                  personalizedSuggestions={personalizedSuggestions}
                  dataTransfer={dataTransfer}
                  setAnalytics={setAnalytics}
                  setAdvertising={setAdvertising}
                  setPersonalizedSuggestions={setPersonalizedSuggestions}
                  setDataTransfer={setDataTransfer}
                  personalizedSuggestionsContent={personalizedSuggestionsContent}
                  dataTransferContent={dataTransferContent}
                  printerWelcome={printerWelcome}
                  defaultConsentContent={defaultConsentContent}
                  appAnalyticsTitle={appAnalyticsTitle}
                  advertisingTitle={advertisingTitle}
                  personalizedTitle={personalizedTitle}
                  appOptionalDataUseDesc={appOptionalDataUseDesc}
                  setRenderPageAnyToggleClick={setRenderPageAnyToggleClick}
                  appOptionalTitle={appOptionalTitle}
                  appDataTransferTitle={appDataTransferTitle}
                  hasAppNameWJA={hasAppNameWJA}
                />
              );
            }
          })()}
        </ToggleLayout>
        {userConsents ? (
          footer && footer.match(/<[a-z]+>/) ? (
            <Footer
              isDesktop={isDesktop}
              dangerouslySetInnerHTML={{ __html: footer }}
              isRTL={isRTL}
            />
          ) : (
            footer
          )
        ) : (
          ''
        )}
      </ReTargetContentContainer>
      <ConsentsButtonContainer
        id="consents-button-container"
        isWJA={hasAppNameWJA}
        isDesktop={isDesktop}
      >
        <StyledButton
          onClick={handleClick}
          loading={loading}
          disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
          small={smallButton}
          id="consent-continue-button"
        >
          {onBoard ? continueButton : saveButton}
        </StyledButton>
        {isShowBackButton && (
          <StyledButton
            onClick={backClick}
            appearance="secondary"
            small={smallButton}
            id="consent-back-button"
          >
            {backButton}
          </StyledButton>
        )}
      </ConsentsButtonContainer>
    </ReTargetContainer>
  );
};

export default PrivacySettings;
